/**
 * Environment partition for staged-upload object keys.
 *
 * One `api-staging` bucket and one `api_uploads` table serve production,
 * preview and local, so this segment is the only thing keeping one
 * environment's sweep from deleting another's in-flight uploads.
 *
 * `NODE_ENV` cannot make that distinction: Vercel preview builds and
 * Trigger.dev preview deploys both run as `production`, which is precisely the
 * pair that has to stay apart. So the value is resolved from a signal that can
 * actually tell them apart, and it fails *safe* — an environment we cannot
 * identify gets its own namespace rather than inheriting production's.
 *
 * Set `STORAGE_ENV` explicitly in Vercel and in Trigger.dev for every
 * environment. `VERCEL_ENV` covers Vercel without it; nothing covers
 * Trigger.dev, which is why an unidentified environment refuses to sweep (see
 * sweepStagedUploads).
 *
 * Scope is the staging bucket. `utils/tasks/s3-storage.ts` keeps its own
 * NODE_ENV-derived `trainings/<env>/` prefix deliberately: it has years of live
 * objects under it, so changing how that one resolves would strand them.
 */

/** No positive signal for which environment this is. */
export const UNRESOLVED_STORAGE_ENV = 'unset';

function resolveStorageEnvPrefix(): string {
  const explicit = process.env.STORAGE_ENV?.trim().toLowerCase();
  if (explicit) return explicit;

  // 'production' | 'preview' | 'development', set by Vercel on every deploy.
  const vercelEnv = process.env.VERCEL_ENV?.trim().toLowerCase();
  if (vercelEnv) return vercelEnv === 'production' ? 'prod' : vercelEnv;

  if (process.env.NODE_ENV !== 'production') return 'dev';

  return UNRESOLVED_STORAGE_ENV;
}

export const STORAGE_ENV_PREFIX = resolveStorageEnvPrefix();
